import type { TableMetadata } from '../types/scriptProvider';

export type ResolvedPrimaryKey = {
  readonly name: string;
  readonly columns: readonly string[];
  /** The kind of the index behind the key, where the catalog reports it (SQL
   *  Server `sys.indexes.type_desc`); undefined elsewhere. */
  readonly isClustered?: boolean;
};

/**
 * The PRIMARY KEY a CREATE script spells: the catalog's own constraint — its
 * name and declared column order, from `TableMetadata.constraints` — when the
 * metadata carries one, otherwise the flagged columns under a conventional
 * name.
 *
 * The name is load-bearing for the Table Designer: this script is its
 * ALTER-mode baseline, and a primary-key edit is planned as `DROP CONSTRAINT
 * <name>` + `ADD CONSTRAINT …`. A conventional name (`pk_<table>`,
 * `PK_<table>`) targets a constraint that does not exist whenever the real one
 * is spelled otherwise — the designer's own create mode writes `"PK_<Table>"`
 * (quoted, mixed case), SQL Server names an unnamed key `PK__t__…`, Oracle
 * `SYS_C00…` — so removing or changing the key failed on the DROP (PostgreSQL
 * `42704`, measured) although the definition read correctly.
 *
 * Membership comes from the column flags, not the constraint: the legacy-draft
 * overlay toggles `isPrimaryKey` on live metadata without touching
 * `constraints`, so a key the draft changed keeps the catalog name (which is
 * what the planner drops) with the draft's columns. The catalog's column order
 * is used only when it lists exactly the flagged columns. The order matters
 * on every engine, named key or not: a baseline listing the key in
 * table-column order re-created `PRIMARY KEY (b, a)` as `(a, b)` on the first
 * key edit (measured on MySQL).
 */
export function resolvePrimaryKey(metadata: TableMetadata, conventionalName: string): ResolvedPrimaryKey | undefined {
  const flagged = metadata.columns.filter(column => column.isPrimaryKey).map(column => column.name);
  if (flagged.length === 0) return undefined;
  const declared = metadata.constraints?.find(constraint => constraint.type === 'PRIMARY KEY');
  const sameColumns = declared !== undefined
    && declared.columns.length === flagged.length
    && flagged.every(name => declared.columns.includes(name));
  return {
    name: declared?.name || conventionalName,
    columns: sameColumns ? [...declared.columns] : flagged,
    ...(declared?.isClustered === undefined ? {} : { isClustered: declared.isClustered }),
  };
}
